/**
 * A10A — compatibility-barrel seam. Telemetry / audit-ring event schemas
 * extracted verbatim from the red `editor.ts` (behaviour-preserving move,
 * no redesign). Every symbol here is re-exported from `./editor` so no
 * external import path changes and every moved symbol keeps its EXACT
 * original type identity (re-export, not a redeclaration).
 */
import { z } from "zod";

import { commandSourceSchema } from "./editorPrimitives";

/** JSON scalar permitted in a serialised telemetry/audit payload field. */
const telemetryScalarSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);

/**
 * Flat, scalar-only WIRE projection of a telemetry payload (ТЗ §14 "params").
 *
 * This is the SERIALISED shape the server channel POSTs (and the
 * `telemetryBatch` it batches) — it is deliberately NOT the in-code payload
 * type. The in-code payload is the typed, discriminated `AuditOperationParams`
 * union in `auditTaxonomy.ts` (`describeAuditOperation`'s return); the web
 * `telemetryServerChannel` projects typed→flat at the wire boundary. Kept to
 * JSON scalars so the payload stays serialisable end-to-end (browser → POST →
 * Laravel JSON column) without leaking arbitrary object graphs.
 */
export const telemetryParamsSchema = z.record(z.string(), telemetryScalarSchema);

/**
 * In-memory audit-RING payload schema. Unlike the flat wire
 * `telemetryParamsSchema`, the two grouping discriminants `category` + `op`
 * are REQUIRED: the ring always carries the typed `describeAuditOperation`
 * payload (see `auditTaxonomy.ts`), and requiring the discriminants here is
 * the runtime half of the clobber-protection (the compile-time half is the
 * typed `AuditOperationParams` union + the `extraParams` type that can never
 * carry `category`/`op`). Op-specific scalar fields are validated by the
 * catchall. Defined ahead of `auditEventSchema` so the ring can store it.
 */
export const auditOperationParamsSchema = z
  .object({
    category: z.string().min(1),
    op: z.string().min(1),
  })
  .catchall(telemetryScalarSchema);

// ТЗ §14 "errors": an `error` tone is the command-feed signal for a FAILED
// command / parse error. It is the only tone `toneToStatus` maps to the
// `status:"error"` aggregation bucket (info/success → "ok", warning →
// "warning"), and such an event MUST carry an `errorMessage` (the refine on
// `telemetryEventSchema`). Declared ahead of `auditEventSchema` so the ring
// entry can persist its outcome tone (#353 @codex F7) without a forward ref.
export const telemetryToneSchema = z.enum([
  "info",
  "success",
  "warning",
  "error",
]);

export const auditEventSchema = z.object({
  id: z.string().min(1),
  sequence: z.number().int().positive(),
  commandType: z.string().min(1),
  source: commandSourceSchema,
  actorEmail: z.string().email(),
  summary: z.string().min(1),
  /**
   * #353 @codex F7 — the audit-ring outcome tone, persisted ON the ring entry
   * itself (not only mirrored onto the command-line transcript row). This lets
   * the CC-1a transport recover the tone for events that have NO transcript
   * row — the ТЗ §14 "clicks" family (ui-click / ui-toggle / ui-nav, kept OUT
   * of the transcript) lives ONLY in `audit.uiEvents`, so before this field a
   * `recordUiClick({ tone: "error" })` was projected as `status:"ok"` (the
   * transport, recovering tone only from the transcript, defaulted to "info").
   * Typed to the telemetry tone domain (`TelemetryTone`) so it never drifts
   * from the `toneToStatus` projection. OPTIONAL (not defaulted) so: (a)
   * snapshots persisted before this slice — and the many existing
   * `projectAuditEventToTelemetry` callers / fixtures that supply the lean ring
   * payload — parse without it, and (b) the transport's
   * `event.tone ?? transcript ?? "info"` fallback chain stays live (an absent
   * ring tone falls back to the transcript row, exactly as pre-F7).
   */
  tone: telemetryToneSchema.optional(),
  /**
   * ABB-S2-CONTRACTS — the ORIGINATING CHANNEL of the audit entry: WHICH input
   * surface produced the command this entry records. A NEW field whose value
   * domain is chosen for the audit / history surface — DISTINCT from (and NOT a
   * widening of) the CLOSED `commandSourceSchema`
   * `['toolbar','commandline','scene','session','system']`, which stays a
   * frozen enum (INV-6: a bridge turn still carries `source:'system'`). The
   * channel answers "who drove it" (the remote agent bridge vs the in-app AI
   * panel vs a human vs the command line vs the system itself), so the history
   * view can attribute a `source:'system'` mutation to the agent-bridge actor
   * rather than collapsing every system command into one bucket:
   *   - `agent-bridge`  — a remote local-agent turn over the AGENT-BRIDGE WS
   *     (audited `source:'system'` + a distinct synthetic bridge actor; see the
   *     manifest protocol primer `auditChannel`).
   *   - `ai-panel`      — the in-app AI drafting panel (the safe contour, but
   *     driven by the local user's panel, not the remote bridge).
   *   - `user`          — a direct human interaction (toolbar / scene / grips).
   *   - `command-line`  — a typed command-line submission.
   *   - `system`        — an internal / automatic command with no external
   *     driver (history replay inverse, document-load fixups).
   * OPTIONAL (NOT defaulted) for snapshot-parse backward-compat: every audit
   * entry persisted before this slice — and every existing `pushAuditEvent`
   * caller / fixture that supplies the lean ring payload — MUST still validate
   * without it (mirrors the `tone` / `params` optionality rationale above).
   *
   * STAGED DELIVERY (documented-incomplete subsystem per ПРИОРИТЕТ #1):
   * THIS slice (ABB-S2-CONTRACTS) lands ONLY the typed `channel` field — the
   * contract FOUNDATION + the telemetry projection passthrough. It does NOT yet
   * PRODUCE the value: `pushAuditEvent` (`reducerHelpers.ts:84-100`) hardcodes
   * `actorEmail=currentUser.email` and stamps no channel today, so current /
   * pre-bridge audit + telemetry events still collapse to `source:'system'`
   * with NO channel.
   *   - PRODUCTION lands in ABB-S5-PUSH (design doc line 144): the bridge sets
   *     an ambient "current bridge turn" marker around the synchronous contour
   *     call that `reducerHelpers` reads to override `actorEmail` + stamp
   *     `channel:'agent-bridge'` on the ring (the typed model state, not a
   *     push-only cosmetic). Wiring `pushAuditEvent` / its 184 call sites is
   *     ABB-S5 scope, deliberately NOT folded into this contracts slice.
   *   - SERVER PERSISTENCE is a tracked PHP/DB follow-up: the API ACCEPTS
   *     `channel` (v3 contract validation) but does NOT STORE it — needs a new
   *     migration alongside
   *     `apps/api/database/migrations/2026_06_07_000100_create_telemetry_events_table.php`
   *     (nullable `channel` column) + a `TelemetryEvent` fillable +
   *     `TelemetryIngestController::__invoke` firstOrCreate map
   *     (`apps/api/app/Http/Controllers/Api/TelemetryIngestController.php`) +
   *     an `AdminTelemetrySummaryController` byChannel rollup. The
   *     browser→wire seam (the channel reaches the wire schema) is closed in
   *     ABB-S2; only server-side STORAGE is deferred.
   */
  channel: z
    .enum(["agent-bridge", "ai-panel", "user", "command-line", "system"])
    .optional(),
  /**
   * CC-1c — typed discriminated operation payload (no string-bag). Attached
   * at the `pushAuditEvent` chokepoint via `describeAuditOperation`; the
   * CC-1a transport projects it (typed→flat) onto the telemetry event's
   * `params` at the wire boundary. Optional + defaulted-absent so snapshots
   * persisted before CC-1c parse. Validated by `auditOperationParamsSchema`
   * (discriminants required) — NOT the flat wire `telemetryParamsSchema`.
   */
  params: auditOperationParamsSchema.optional(),
  /**
   * CC-1b — the AFFECTED entity ids of the command this entry records, captured
   * VERBATIM from the source command at the `pushAuditEvent` chokepoint
   * (`affectedEntityIdsForCommand`). This is the per-action EVIDENCE the audit
   * console's detail panel surfaces and the admin `entity_ids` column persists
   * (`AuditRecord.entityIds`).
   *
   * It is a FIRST-CLASS field on the ring entry — NOT folded into the scalar
   * `params` bag — for two reasons: (1) the typed `params` (and its flat wire
   * `telemetryParamsSchema` projection) is scalar-ONLY, so a multi-entity command
   * carries only an aggregate `entityCount` there; the actual id LIST has no valid
   * home in a scalar record. (2) The id list is an AUDIT concern, not a telemetry
   * one — telemetry aggregates by count — so `telemetryEventBaseSchema` OMITS this
   * field (the audit-ring→telemetry projection never carries it).
   *
   * OPTIONAL (not defaulted) so: a lean `pushAuditEvent` for a no-entity command
   * (a view-op / a session command) omits it, and every audit entry persisted
   * before this slice parses without it. The audit projection falls back to the
   * single `params.entityId` for those pre-slice snapshots.
   */
  entityIds: z.array(z.string().min(1)).optional(),
});

/**
 * CC-1a — typed telemetry event model (ТЗ §14: clicks/commands/params/
 * errors/timings + server aggregation).
 *
 * This is the CAD-core source of truth for the server-bound telemetry
 * channel. It is NOT a string bag: it is a versioned, discriminated event
 * that EXTENDS the in-memory `auditEventSchema` payload (id / sequence /
 * source / actorEmail / summary) with the fields ТЗ §14 requires for a
 * server channel — `params`, `durationMs` (timings), `status` +
 * `errorMessage` (errors). The web `telemetryServerChannel` projects each
 * in-memory `pushAuditEvent` entry into one of these, batches them and
 * flushes to the backend; the Laravel ingest endpoint validates against the
 * same shape.
 *
 * The `kind` discriminant separates true editor commands (`command`) from
 * pure view operations (`view-op` — REGEN/REGENALL/ZOOM/PAN) that do NOT
 * mutate geometry and therefore never pass through `geometryShellReducer`.
 * Routing the view-op family through this model is exactly how REGEN's
 * audit event reaches the server channel (CC-1a) without a reducer hook.
 *
 * CC-1e widens the discriminant with a NON-command interaction family — the
 * ТЗ §14 "clicks" axis, distinct from the "commands" axis:
 *   - `ui-click`  — a toolbar / ribbon / quickbar button or panel control was
 *     clicked (carries `target` + `controlId`).
 *   - `ui-toggle` — a drawing-aid setting was toggled (OSNAP / ORTHO / POLAR /
 *     SNAP / GRID); carries `setting` + the resolved `enabled` state.
 *   - `ui-nav`    — the user navigated between surfaces WITHOUT a command — a
 *     panel/tab switch, a ribbon-tab switch, or clicking a layout/space tab
 *     (vs. typing MSPACE/PSPACE, which stays a `command`); carries
 *     `navKind` + `from` + `to`.
 * These never mutate geometry and are pure observability: they flow through
 * the SAME `pushAuditEvent`-family chokepoint into the audit ring so the CC-1a
 * transport carries them, but they are kept OUT of the command-line transcript
 * (no command-feed spam — see `pushUiInteractionAuditEvent`).
 *
 * Bump `TELEMETRY_SCHEMA_VERSION` on any breaking field change so backend
 * ingest can reject / migrate old payloads. This Zod schema is the PRODUCER
 * contract: the web client always emits the CURRENT version, so the literal
 * tracks the constant and never widens to a union. The version-gated
 * compat/migrate path lives in the CONSUMER (`TelemetryIngestController`),
 * which enumerates every supported version explicitly.
 *
 * Version history:
 *   - v1 — initial CC-1a shape; per-event identity was (id, timestampMs).
 *   - v2 — #284-3/#293-1: REQUIRED `clientEventUid` (collision-resistant
 *     client-minted dedupe key). Adding a required field is a breaking wire
 *     change, so the version is bumped; an older cached schema-v1 client
 *     posting after an API deploy is NOT rejected — ingest accepts v1 events
 *     without a uid and synthesizes the legacy `(id, timestampMs)` dedupe key
 *     for them (with v1's documented reset-same-ms weakness), while v2 events
 *     must carry a non-empty uid.
 *   - v3 — #353: the `tone` enum gained `"error"` (ТЗ §14 failed-command / parse
 *     error → `status:"error"`). Adding an enum value the prior wire shape did
 *     not accept is a breaking change for a v2-pinned consumer, so the version
 *     is bumped; ingest version-gates so a rolling deploy does not reject the
 *     new tone as an invalid v2 payload (and the retried head batch can't block
 *     later telemetry behind it).
 */
export const TELEMETRY_SCHEMA_VERSION = 3 as const;

export const telemetryEventKindSchema = z.enum([
  "command",
  "view-op",
  "ui-click",
  "ui-toggle",
  "ui-nav",
]);

/**
 * CC-1e — navigation domain for a `ui-nav` telemetry event. Distinguishes a
 * panel switch (properties↔history↔ai) from a ribbon-tab switch, a layout/
 * space tab click, and a paper↔viewport layout-context switch.
 */
export const telemetryNavKindSchema = z.enum([
  "panel",
  "space",
  "layout-context",
  "ribbon-tab",
]);

/** Resolved outcome of the emitting command/op — drives error aggregation. */
export const telemetryStatusSchema = z.enum(["ok", "warning", "error"]);

/**
 * Shared telemetry fields, DERIVED from `auditEventSchema` so the id /
 * sequence / source / actorEmail / summary validators can never drift from
 * the in-memory audit ring they are projected out of. `commandType` is
 * variant-specific (a `command` carries `commandType`, a `view-op` carries
 * `opType`), so it is omitted here and re-added on the discriminated arms.
 */
const telemetryEventBaseSchema = auditEventSchema
  // `commandType` is variant-specific (re-added per discriminated arm). `entityIds`
  // is the AUDIT-only affected-id evidence (CC-1b) — telemetry aggregates by count
  // (`params.entityCount`), never the id list — so it is omitted here and the
  // audit-ring→telemetry projection never carries it onto the wire.
  .omit({ commandType: true, entityIds: true })
  .extend({
    schemaVersion: z.literal(TELEMETRY_SCHEMA_VERSION),
    /**
     * CC-1a idempotency — a collision-resistant per-event identifier the client
     * mints ONCE when the audit entry is projected to a telemetry event (the
     * `telemetryServerChannel` projection). It is the server-side dedupe key
     * (`user_email + client_event_uid` unique), so it must be:
     *   - STABLE across a lost-response retry of the SAME event (the channel
     *     re-POSTs the identical object, carrying the same uid → no duplicate
     *     row), and
     *   - DISTINCT for two genuinely different events even when they reuse the
     *     same `id` (`audit-N`) AND land in the same `timestampMs` after an
     *     `editorStore.reset()` (the audit sequence — and thus `audit-N` —
     *     restarts at 1). Without it the `(id, timestampMs)`-only key would
     *     silently drop the second legitimate event as a retry duplicate.
     */
    clientEventUid: z.string().min(1),
    tone: telemetryToneSchema,
    status: telemetryStatusSchema,
    params: telemetryParamsSchema.optional(),
    /** Wall-clock duration of the command/op in ms (ТЗ §14 "timings"). */
    durationMs: z.number().nonnegative().optional(),
    /** Populated only when `status === "error"` (ТЗ §14 "errors"). */
    errorMessage: z.string().min(1).optional(),
    /** Emit time (ms since epoch) — server-side ordering / aggregation key. */
    timestampMs: z.number().int().nonnegative(),
  });

export const telemetryCommandEventSchema = telemetryEventBaseSchema.extend({
  kind: z.literal("command"),
  commandType: z.string().min(1),
});

export const telemetryViewOpEventSchema = telemetryEventBaseSchema.extend({
  kind: z.literal("view-op"),
  /** View-op literal (e.g. "regen", "regenall", "zoom", "pan"). */
  opType: z.string().min(1),
});

/**
 * CC-1e — UI click telemetry (ТЗ §14 "clicks"). A toolbar / ribbon / quickbar
 * button or panel control was clicked. Typed discriminated payload (NO string
 * bag): `target` is the surface ("ribbon" / "quickbar" / "toolbar" / "panel")
 * and `controlId` is the stable control identifier ("quickbar-undo",
 * "ribbon-action-LINE", …).
 */
export const telemetryUiClickEventSchema = telemetryEventBaseSchema.extend({
  kind: z.literal("ui-click"),
  target: z.string().min(1),
  controlId: z.string().min(1),
});

/**
 * CC-1e — drawing-aid toggle telemetry. A 0/1 drawing aid (OSNAP / ORTHO /
 * POLAR / SNAP / GRID, by sysvar name) was flipped. `enabled` is the resolved
 * state AFTER the toggle.
 */
export const telemetryUiToggleEventSchema = telemetryEventBaseSchema.extend({
  kind: z.literal("ui-toggle"),
  setting: z.string().min(1),
  enabled: z.boolean(),
});

/**
 * CC-1e — non-command navigation telemetry. The user moved between surfaces
 * via a click (panel/tab switch, ribbon-tab switch, layout/space tab click)
 * rather than a command. `from`/`to` are the origin/destination identifiers
 * within the `navKind` domain.
 */
export const telemetryUiNavEventSchema = telemetryEventBaseSchema.extend({
  kind: z.literal("ui-nav"),
  navKind: telemetryNavKindSchema,
  from: z.string().min(1),
  to: z.string().min(1),
});

export const telemetryEventSchema = z
  .discriminatedUnion("kind", [
    telemetryCommandEventSchema,
    telemetryViewOpEventSchema,
    telemetryUiClickEventSchema,
    telemetryUiToggleEventSchema,
    telemetryUiNavEventSchema,
  ])
  // ТЗ §14 "errors": an error-status event MUST carry the message, otherwise
  // the server-side error aggregation has nothing to group on.
  .refine(
    (event) =>
      event.status !== "error" || (event.errorMessage ?? "").length > 0,
    {
      message: 'errorMessage is required when status is "error"',
      path: ["errorMessage"],
    },
  )
  // ТЗ §14 "errors" (#353 @codex F2): the v3-only `error` tone is the FAILED-
  // command / parse-error signal — it must land in the `status:"error"`
  // aggregation bucket. Without this refine a `tone:"error"` event could carry
  // `status:"ok"`/`"warning"` (the error-message refine above keys off `status`
  // only), producing a row tagged error-tone but counted in a non-error status
  // bucket — and, because that path never trips the `status==="error"` arm, it
  // could also omit `errorMessage`. Tie the tone to the status so the two
  // error signals can never disagree.
  .refine((event) => event.tone !== "error" || event.status === "error", {
    message: 'status must be "error" when tone is "error"',
    path: ["status"],
  });

/** Wire shape for a batched flush to the backend ingest endpoint. */
export const telemetryBatchSchema = z.object({
  schemaVersion: z.literal(TELEMETRY_SCHEMA_VERSION),
  events: z.array(telemetryEventSchema).min(1).max(500),
});
